import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { ICompanyDataModel } from '../../../../../data-models/company.data-model';
import { companySyncCacheAtom } from '../../../../../services/state/CompanyStateJ';
import { useRespondentApprovalStatuses } from '../../../../../services/queries-hooks/RespondentApprovalStatusQueriesHooks';
import { hasNonRejectedRespondents } from '../../../../../util/respondentStatus';
import { useRespondentApprovalsEnabled } from '../../../hooks/useRespondentApprovalsEnabled';
import { selectedKpiCompaniesStateDCJ } from '../AllCompaniesStateJ';
import { validateCompanyData } from './template-assignment-utils';

/**
 * `validateCompanyData` bound to the Starbreaker respondent statuses. The status query is enabled for
 * consolidated Starbreaker tenants only ({@link useRespondentApprovalsEnabled}); elsewhere the lookup
 * is empty and this degrades to the Maggie-only rule.
 */
export function useValidateCompanyData(): (company: ICompanyDataModel) => boolean {
  const respondentApprovalsEnabled = useRespondentApprovalsEnabled();
  const { data: statusLookup } = useRespondentApprovalStatuses(respondentApprovalsEnabled);

  return useCallback(
    (company: ICompanyDataModel) =>
      validateCompanyData(
        company,
        respondentApprovalsEnabled && hasNonRejectedRespondents(statusLookup?.get(company.id))
      ),
    [respondentApprovalsEnabled, statusLookup]
  );
}

/**
 * Ids of the selected companies still missing data, gating the "Company Settings" step and its Next
 * button. Reads companies from {@link companySyncCacheAtom}, not `getCompanySyncIfResolved`: this must
 * recompute when a company is filled in or the status query resolves, and an imperative store read
 * registers no dependency for either.
 */
export function useCompaniesWithMissingData(): Set<number> {
  const selectedCompanies = useAtomValue(selectedKpiCompaniesStateDCJ);
  const companyCache = useAtomValue(companySyncCacheAtom);
  const validate = useValidateCompanyData();

  return useMemo(() => {
    const invalid = new Set<number>();
    for (const { id } of selectedCompanies) {
      const company = companyCache.get(id);
      if (company && !validate(company)) {
        invalid.add(id);
      }
    }
    return invalid;
  }, [companyCache, selectedCompanies, validate]);
}
